import React, { useEffect, useState } from "react";
import { Table, Tooltip } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import Preview from "../../pages/planning/VisitBoard/TableView/modals/Preview";
import { httpService } from "../../../services/httpService.service";
dayjs.extend(isBetween);

dayjs.extend(isBetween);

const CalendarViewWeekMode = ({
  selectedWeek,
  selectedMonth,
  selectedYear,
}) => {
  const projectID = localStorage.getItem("currentProjectID");
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [siteVisits, setSiteVisits] = useState([]);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState("");
  const [year, month] = selectedMonth.split("-").map(Number);
  const monthStart = dayjs(new Date(year, month - 1, 1));
  const startOfWeek = monthStart
    .startOf("week")
    .add((selectedWeek - 1) * 7, "day");

  const endOfWeek = startOfWeek.add(6, "day");

  const startDate = startOfWeek.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");
  const endDate = endOfWeek.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 700);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getTrainingDate = (weekStart, dayOffset) => {
    if (dayOffset === "-") return null; // Ignore invalid dates
    return new Date(
      new Date(weekStart).setDate(
        new Date(weekStart).getDate() + parseInt(dayOffset)
      )
    )
      .toISOString()
      .split("T")[0]; // Format as YYYY-MM-DD
  };

  useEffect(() => {
    const params = { ProjectId: projectID, startDate, endDate };
    const queryString = new URLSearchParams(params).toString();

    httpService.get(`/api/Site/trainingAnalysis?${queryString}`, (res) => {
      const data = res?.data;

      const groupedData = data?.reduce((acc, siteVisit) => {
        const state = siteVisit.stateName || "Unknown";
        if (!acc[state]) acc[state] = [];

        siteVisit.siteVisitTraining.forEach((visit) => {
          const trainingDatesArray = Array.isArray(visit.trainingDates)
            ? visit.trainingDates
            : [visit.trainingDates];

          const trainingDateEntries = trainingDatesArray.map((trainingDate) => {
            return {
              key: visit.siteVisitId,
              name: visit?.siteVisitName || "Unnamed Visit",
              estimatedDayOfVisit: visit.visitPlanned || "",
              trainingDates: trainingDate,
            };
          });
          acc[state].push(...trainingDateEntries);
        });

        return acc;
      }, {});

      const structuredData = Object.entries(groupedData || {}).map(
        ([stateName, visits]) => {
          const visitsWithDates = visits.flatMap((visit) => {
            const visitEntries = [];

            // Ensure estimated day of visit is a full date
            if (visit?.estimatedDayOfVisit) {
              visitEntries.push({
                siteVisitId: visit?.key,
                siteVisitName: visit?.name,
                date: visit?.estimatedDayOfVisit, // Already a full date
                type: "visit",
                color: "#A7587F",
              });
            }

            // Ensure training dates are correctly formatted
            if (visit?.trainingDates) {
              const formattedTrainingDate = isNaN(visit.trainingDates)
                ? visit.trainingDates // Already a full date
                : getTrainingDate(selectedWeek, visit.trainingDates); // Convert number to a full date

              visitEntries.push({
                siteVisitId: visit?.key,
                siteVisitName: visit?.name,
                date: formattedTrainingDate,
                type: "training",
                color: "#B9A5AF",
              });
            }

            // Ensure unique dates are stored correctly
            const uniqueDates = [
              ...new Set([visit.estimatedDayOfVisit, visit.trainingDates]),
            ];

            return uniqueDates.map((date) => ({
              siteVisitId: visit.key,
              siteVisitName: visit.name,
              date: isNaN(date) ? date : getTrainingDate(selectedWeek, date),
              type:
                visit.estimatedDayOfVisit == date &&
                visit.trainingDates.includes(date)
                  ? "both"
                  : visit.estimatedDayOfVisit == date
                  ? "visit"
                  : "training",
              color: visit.color,
            }));
          });

          const totalVisits = visitsWithDates.length || 0;
          const uniqueDates = new Set(
            visitsWithDates.map((visit) => visit.date)
          );

          return {
            state: stateName,
            visits: visitsWithDates,
            countOfVisits:
              totalVisits > 1
                ? `${totalVisits} visits`
                : `${totalVisits} visit`,
            totalDays:
              uniqueDates.size > 1
                ? `${uniqueDates.size} days`
                : `${uniqueDates.size} day`,
          };
        }
      );

      const filteredData = structuredData
        .map((record) => {
          const filteredVisits = record.visits.filter((visit) =>
            dayjs(visit.date).isBetween(startDate, endDate, "day", "[]")
          );

          return filteredVisits.length
            ? {
                state: record.state,
                visits: filteredVisits,
                countOfVisits: record.countOfVisits,
                totalDays: record.totalDays,
              }
            : null;
        })
        .filter(Boolean);

      setSiteVisits(filteredData);
    });
  }, [startDate, endDate]);

  const generateColumns = () => [
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      fixed: isSmallScreen ? false : "left",
      width: 200,
    },
    {
      title: "# of visits",
      dataIndex: "countOfVisits",
      key: "countOfVisits",
      fixed: isSmallScreen ? false : "left",
      width: 100,
      align: "center",
    },
    {
      title: "Total days",
      dataIndex: "totalDays",
      key: "totalDays",
      fixed: isSmallScreen ? false : "left",
      width: 100,
      align: "center",
      className: isSmallScreen ? "" : "cellsRightShadow",
    },
    ...Array.from({ length: 7 }, (_, i) => {
      const currentDay = startOfWeek.add(i, "day");
      return {
        title: `${currentDay.format("ddd")} ${currentDay.format("DD")}`,
        key: `day${i + 1}`,
        align: "center",
        className: "dataCell",
        render: (_, record) => renderCell(currentDay, record),
      };
    }),
  ];

  const renderCell = (currentDay, record) => {
    const visitsOfCurrentDay = [];
    const trainingsOfCurrentDay = [];

    record.visits.forEach((visit) => {
      if (dayjs(visit.date).isSame(currentDay, "day")) {
        if (visit.type === "visit") {
          visitsOfCurrentDay.push(visit);
        } else if (visit.type === "training") {
          trainingsOfCurrentDay.push(visit);
        }
      }
    });

    const backgroundColor =
      visitsOfCurrentDay.length && trainingsOfCurrentDay.length
        ? "#EF7DB5"
        : visitsOfCurrentDay.length
        ? "#A7587F"
        : trainingsOfCurrentDay.length
        ? "#B9A5AF"
        : "transparent";

    const boxShadow =
      visitsOfCurrentDay.length || trainingsOfCurrentDay.length
        ? "1px 0px 5px rgba(0, 0, 0, 0.3)"
        : "none";

    const isWeekend = currentDay.day() === 0 || currentDay.day() === 6;
    const lastMonthDay = currentDay.endOf("month");
    const isLastDayOfMonth = currentDay.isSame(lastMonthDay, "day");

    const weekendStyle =
      isWeekend && !visitsOfCurrentDay.length && !trainingsOfCurrentDay.length
        ? { backgroundColor: "rgba(247, 236, 236, 0.84)" }
        : {};
    const lastDayStyle = isLastDayOfMonth
      ? { borderRight: "2px solid black", opacity: ".4" }
      : {};

    return {
      props: {
        style: {
          backgroundColor,
          boxShadow,
          ...weekendStyle,
          ...lastDayStyle,
        },
      },
      children: (
        <Tooltip
          title={
            visitsOfCurrentDay.length || trainingsOfCurrentDay.length
              ? `Visits: ${visitsOfCurrentDay.length}, Trainings: ${trainingsOfCurrentDay.length}`
              : "No site visits or training on this date"
          }
        >
          <div className="cellWithData">
            {visitsOfCurrentDay.length > 0 && (
              <>
                <p
                  className="counter m-0 mb-1"
                  style={{ color: backgroundColor }}
                >
                  {visitsOfCurrentDay.length} Visits
                </p>
                {visitsOfCurrentDay.map((visit) => (
                  <Tooltip key={visit.siteVisitId} title={visit.siteVisitName}>
                    <a
                      type="button"
                      onClick={() => handleSiteVisitClick(visit.siteVisitId)}
                      className="w-100 text-truncate m-0 p-0"
                    >
                      {visit.siteVisitName}
                    </a>
                  </Tooltip>
                ))}
              </>
            )}
            {trainingsOfCurrentDay.length > 0 && (
              <>
                <p
                  className="counter m-0 mb-1"
                  style={{ color: backgroundColor }}
                >
                  {trainingsOfCurrentDay.length} Trainings
                </p>
                {trainingsOfCurrentDay.map((visit) => (
                  <Tooltip key={visit.siteVisitId} title={visit.siteVisitName}>
                    <a
                      type="button"
                      onClick={() => handleSiteVisitClick(visit.siteVisitId)}
                      className="w-100 text-truncate m-0 p-0"
                    >
                      {visit.siteVisitName}
                    </a>
                  </Tooltip>
                ))}
              </>
            )}
          </div>
        </Tooltip>
      ),
    };
  };

  const handleSiteVisitClick = (id) => {
    setSelectedSiteVisit(id);
    setShowPreviewModal(true);
  };

  return (
    <>
      <Preview
        visible={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        siteVisitCardId={selectedSiteVisit}
      />
      <Table
        rowKey="state"
        columns={siteVisits?.length ? generateColumns() : []}
        dataSource={siteVisits}
        bordered
        pagination={false}
        scroll={{ x: "max-content", y: 500 }}
        tableLayout="fixed"
        className="w-100"
      />
    </>
  );
};

export default CalendarViewWeekMode;
